import { useCallback, useEffect, useMemo, useState } from "react";
import DataSourceDetailPage from "../dataSources/DataSourceDetailPage";
import { buildDataSourceCatalog } from "../dataSources/buildDataSourceCatalog";
import { readDataSourceDetailFromHash } from "../../../../utils/dataSourceDetailRoutes";
import RealEstateSourceControlsTable from "./RealEstateSourceControlsTable";
import WeatherSourceControlsTable from "../weather/WeatherSourceControlsTable";
import FoodRetailSourceControlsTable from "../food/FoodRetailSourceControlsTable";
import FoodSipsaSourceControlsTable from "../food/FoodSipsaSourceControlsTable";
import FoodInputReferenceSourceControlsTable from "../food/FoodInputReferenceSourceControlsTable";
import FoodOsmPoiSourceControlsTable from "../food/FoodOsmPoiSourceControlsTable";
import FoodBranchLocatorSourceControlsTable from "../food/FoodBranchLocatorSourceControlsTable";
import FoodSourceControlsGrid from "../dataSources/FoodSourceControlsGrid";
import TransportSourceControlsTable from "../transport/TransportSourceControlsTable";
import CultureSourceControlsTable from "../culture/CultureSourceControlsTable";
import VehicleMarketSourceControlsTable from "../automotive/VehicleMarketSourceControlsTable";
import FinanceSourceControlsTable from "../finance/FinanceSourceControlsTable";
import Ga4AnalyticsSourceControlsTable from "../analytics/Ga4AnalyticsSourceControlsTable";
import VendorDirectorySourceControlsTable from "../casamapa/VendorDirectorySourceControlsTable";
import {
  TYPE_FILTER_OPTIONS,
  buildSourceSummary,
  buildTypeCounts,
} from "./dataSourceControlsSummary";
import { resolveAdminDataSourcesTypeFilter } from "./adminDataSourcesFilter";
import { useDataSourceControls } from "./useDataSourceControls";
import { useAuth } from "../../../../auth/useAuth";
import { ADMIN_PERMISSIONS } from "../../../../auth/adminPermissions";
import AdminStatePanel from "../layout/AdminStatePanel";
import {
  filterRetailSupermarketDataHealthSources,
  filterSipsaDataHealthSources,
  filterInputReferenceDataHealthSources,
  filterOsmPoiDataHealthSources,
  filterBranchLocatorDataHealthSources,
} from "../../../../constants/foodSourceConstants";

const STATUS_FILTER_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "healthy", label: "Healthy" },
  { value: "running", label: "Running" },
  { value: "queued", label: "Queued" },
  { value: "failed", label: "Failed" },
  { value: "warning", label: "Warning" },
  { value: "never_run", label: "Never run" },
  { value: "disabled", label: "Disabled" },
];

const RealEstateSourceControlsPage = ({
  onClose,
  onOpenDataHealth,
  onOpenModeration,
  onOpenPropertyImport,
  onOpenRealEstateSources,
  onOpenVehicleSources,
  onNavigateVehicleWorkflow,
  onOpenSourceQuality,
  onOpenImageHealth,
  onOpenImageCache,
  onOpenMediaQualityDashboard,
  initialTypeFilter = "all",
}) => {
  const {
    realEstateSources,
    setRealEstateSources,
    weatherSources,
    setWeatherSources,
    cultureSources,
    setCultureSources,
    vehicleMarketSources,
    vehicleMarketErrorMessage,
    cultureErrorMessage,
    financeSources,
    setFinanceSources,
    financeErrorMessage,
    ga4Sources,
    setGa4Sources,
    ga4ErrorMessage,
    domainSources,
    setDomainSources,
    isLoading,
    isSyncing,
    errorMessage,
    realEstateErrorMessage,
    weatherErrorMessage,
    toast,
    showToast,
    loadSources,
    handleSyncRegistry,
    hasRunningSource,
  } = useDataSourceControls();
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManageRealEstateSources =
    isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_MANAGE);

  const [detailRoute, setDetailRoute] = useState(() => readDataSourceDetailFromHash());
  useEffect(() => {
    const sync = () => setDetailRoute(readDataSourceDetailFromHash());
    window.addEventListener("hashchange", sync);
    return () => window.removeEventListener("hashchange", sync);
  }, []);

  const catalog = useMemo(
    () =>
      buildDataSourceCatalog({
        realEstateSources,
        weatherSources,
        cultureSources,
        vehicleMarketSources,
        financeSources,
        ga4Sources,
        foodSources: domainSources.food,
        transportSources: domainSources.transport,
        vendorDirectorySources: domainSources.vendor_directory,
      }),
    [
      realEstateSources,
      weatherSources,
      cultureSources,
      vehicleMarketSources,
      financeSources,
      ga4Sources,
      domainSources,
    ]
  );

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [enabledFilter, setEnabledFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState(() =>
    resolveAdminDataSourcesTypeFilter(initialTypeFilter)
  );
  const filters = useMemo(
    () => ({
      search: search.trim(),
      statusFilter,
      enabledFilter,
    }),
    [search, statusFilter, enabledFilter]
  );

  const typeCounts = useMemo(
    () =>
      buildTypeCounts(
        realEstateSources,
        weatherSources,
        domainSources,
        vehicleMarketSources.length,
        financeSources,
        ga4Sources
      ),
    [realEstateSources, weatherSources, domainSources, vehicleMarketSources, financeSources, ga4Sources]
  );

  const summary = useMemo(
    () =>
      buildSourceSummary(
        realEstateSources,
        weatherSources,
        typeFilter,
        domainSources,
        vehicleMarketSources,
        financeSources,
        ga4Sources
      ),
    [
      realEstateSources,
      weatherSources,
      typeFilter,
      domainSources,
      vehicleMarketSources,
      financeSources,
      ga4Sources,
    ]
  );

  const filterDomainSources = useCallback(
    (sources) => {
      return (sources || []).filter((source) => {
        if (filters.search) {
          const haystack = `${source.sourceName} ${source.sourceCode}`.toLowerCase();
          if (!haystack.includes(filters.search.toLowerCase())) {
            return false;
          }
        }
        if (filters.enabledFilter === "enabled" && !source.isActive) {
          return false;
        }
        if (filters.enabledFilter === "disabled" && source.isActive) {
          return false;
        }
        if (filters.statusFilter !== "all") {
          const status = String(source.healthStatus || "").toLowerCase();
          if (filters.statusFilter === "healthy" && status !== "healthy") return false;
          if (filters.statusFilter === "failed" && status !== "failed" && status !== "failing") {
            return false;
          }
          if (filters.statusFilter === "warning" && status !== "stale" && status !== "warning") {
            return false;
          }
          if (filters.statusFilter === "never_run" && status !== "not_configured" && status !== "discovery_pending") {
            return false;
          }
          if (filters.statusFilter === "disabled" && status !== "disabled") return false;
        }
        return true;
      });
    },
    [filters]
  );

  const showRealEstateTable = typeFilter === "all" || typeFilter === "real_estate";
  const showWeatherTable = typeFilter === "all" || typeFilter === "weather";
  const showFoodTable = typeFilter === "all" || typeFilter === "food";
  const showTransportTable = typeFilter === "all" || typeFilter === "transport";
  const showCultureTable = typeFilter === "all" || typeFilter === "culture";
  const showVendorDirectoryTable = typeFilter === "all" || typeFilter === "vendor_directory";
  const showVehicleMarketTable = typeFilter === "all" || typeFilter === "vehicle_market";
  const showFinanceTable = typeFilter === "all" || typeFilter === "finance";
  const showAnalyticsTable = typeFilter === "all" || typeFilter === "analytics";
  const hideEmptySections = typeFilter === "all";

  const filteredFoodSources = useMemo(
    () => filterDomainSources(domainSources.food),
    [domainSources.food, filterDomainSources]
  );
  const filteredRetailFoodSources = useMemo(
    () => filterRetailSupermarketDataHealthSources(filteredFoodSources),
    [filteredFoodSources]
  );
  const filteredSipsaFoodSources = useMemo(
    () => filterSipsaDataHealthSources(filteredFoodSources),
    [filteredFoodSources]
  );
  const filteredInputReferenceFoodSources = useMemo(
    () => filterInputReferenceDataHealthSources(filteredFoodSources),
    [filteredFoodSources]
  );
  const filteredOsmPoiFoodSources = useMemo(
    () => filterOsmPoiDataHealthSources(filteredFoodSources),
    [filteredFoodSources]
  );
  const filteredBranchLocatorFoodSources = useMemo(
    () => filterBranchLocatorDataHealthSources(filteredFoodSources),
    [filteredFoodSources]
  );
  const filteredNonRetailFoodSources = useMemo(
    () =>
      filteredFoodSources.filter(
        (source) =>
          !filteredRetailFoodSources.includes(source) &&
          !filteredSipsaFoodSources.includes(source) &&
          !filteredInputReferenceFoodSources.includes(source) &&
          !filteredOsmPoiFoodSources.includes(source) &&
          !filteredBranchLocatorFoodSources.includes(source)
      ),
    [
      filteredFoodSources,
      filteredRetailFoodSources,
      filteredSipsaFoodSources,
      filteredInputReferenceFoodSources,
      filteredOsmPoiFoodSources,
      filteredBranchLocatorFoodSources,
    ]
  );
  const filteredTransportSources = useMemo(
    () => filterDomainSources(domainSources.transport),
    [domainSources.transport, filterDomainSources]
  );
  const filteredCultureSources = useMemo(() => {
    let list = [...(cultureSources || [])];

    if (filters.search) {
      const query = filters.search.toLowerCase();
      list = list.filter(
        (source) =>
          source.sourceName?.toLowerCase().includes(query) ||
          source.sourceKey?.toLowerCase().includes(query) ||
          source.sourceCode?.toLowerCase().includes(query)
      );
    }

    if (filters.enabledFilter === "enabled") {
      list = list.filter((source) => source.enabled || source.isActive);
    } else if (filters.enabledFilter === "disabled") {
      list = list.filter((source) => !source.enabled && !source.isActive);
    }

    return list;
  }, [cultureSources, filters]);
  const hasActiveFilters =
    Boolean(search.trim()) || statusFilter !== "all" || enabledFilter !== "all";

  const clearFilters = useCallback(() => {
    setSearch("");
    setStatusFilter("all");
    setEnabledFilter("all");
  }, []);

  const showSourceTables = !isLoading;

  if (detailRoute) {
    return (
      <DataSourceDetailPage
        domainId={detailRoute.domainId}
        sourceKey={detailRoute.sourceKey}
        catalog={catalog}
        isLoading={isLoading}
        onRefresh={loadSources}
        showToast={showToast}
        canManage={canManageRealEstateSources}
        toast={toast}
      />
    );
  }

  return (
    <div className="admin-dashboard admin-source-controls-dash">
      {toast ? (
        <div
          className={`admin-re-toast admin-re-toast--${toast.tone}`}
          role="status"
          aria-live="polite"
        >
          {toast.message}
        </div>
      ) : null}

      <header className="admin-dashboard__header admin-source-controls-dash__header">
        <div className="admin-source-controls-dash__header-copy">
          <h1 className="admin-dashboard__title">Data Sources</h1>
          <p className="admin-source-controls-dash__subtitle">
            See which sources are collecting, when they last ran, and change how they collect.
          </p>
        </div>
        <div className="admin-dashboard__header-actions admin-source-controls-dash__header-actions">
          <button
            type="button"
            className="admin-dashboard__action-btn"
            onClick={() => loadSources()}
            disabled={isLoading}
          >
            Refresh
          </button>
          {canManageRealEstateSources ? (
            <button
              type="button"
              className="admin-dashboard__action-btn admin-dashboard__action-btn--primary"
              onClick={handleSyncRegistry}
              disabled={isSyncing}
            >
              {isSyncing ? "Updating…" : "Update real estate sources"}
            </button>
          ) : null}
          {onClose ? (
            <button type="button" className="admin-dashboard__action-btn" onClick={onClose}>
              ← Dashboard
            </button>
          ) : null}
        </div>
      </header>

      <section className="admin-source-controls-dash__stats" aria-label="Source summary">
        <div className="admin-source-controls-dash__stat">
          <span className="admin-source-controls-dash__stat-label">Total sources</span>
          <span className="admin-source-controls-dash__stat-value">{summary.total}</span>
        </div>
        <div className="admin-source-controls-dash__stat">
          <span className="admin-source-controls-dash__stat-label">Turned on</span>
          <span className="admin-source-controls-dash__stat-value admin-source-controls-dash__stat-value--green">
            {summary.enabled}
          </span>
        </div>
        <div className="admin-source-controls-dash__stat">
          <span className="admin-source-controls-dash__stat-label">Have collected data</span>
          <span className="admin-source-controls-dash__stat-value">{summary.collecting ?? 0}</span>
        </div>
        <div className="admin-source-controls-dash__stat">
          <span className="admin-source-controls-dash__stat-label">On a schedule</span>
          <span className="admin-source-controls-dash__stat-value">{summary.scheduled}</span>
        </div>
        <div className="admin-source-controls-dash__stat">
          <span className="admin-source-controls-dash__stat-label">Running</span>
          <span
            className={`admin-source-controls-dash__stat-value${
              hasRunningSource ? " admin-source-controls-dash__stat-value--active" : ""
            }`}
          >
            {hasRunningSource ? <span className="admin-dash__pulse" aria-hidden="true" /> : null}
            {summary.running}
          </span>
        </div>
        {summary.failed > 0 ? (
          <div className="admin-source-controls-dash__stat admin-source-controls-dash__stat--alert">
            <span className="admin-source-controls-dash__stat-label">Failed</span>
            <span className="admin-source-controls-dash__stat-value admin-source-controls-dash__stat-value--red">
              {summary.failed}
            </span>
          </div>
        ) : null}
      </section>

      <section className="admin-dash__card admin-dash__card--wide admin-source-controls-dash__table-card">
        <div className="admin-source-controls-dash__control-bar">
          <div
            className="admin-source-controls-dash__type-chips"
            role="tablist"
            aria-label="Source type"
          >
            {TYPE_FILTER_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                role="tab"
                aria-selected={typeFilter === option.value}
                className={`admin-source-controls-dash__type-chip${
                  typeFilter === option.value ? " is-active" : ""
                }`}
                onClick={() => setTypeFilter(option.value)}
              >
                {option.label}
                <span className="admin-source-controls-dash__type-chip-count">
                  {typeCounts[option.value] ?? 0}
                </span>
              </button>
            ))}
          </div>

          <div className="admin-source-controls-dash__refine">
            <label className="admin-source-controls-dash__search-wrap">
              <span className="sr-only">Search sources</span>
              <input
                type="search"
                className="admin-source-controls-dash__search"
                placeholder="Search by name or code…"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </label>
            <label className="admin-source-controls-dash__field">
              <span className="admin-source-controls-dash__field-label">Status</span>
              <select
                className="admin-source-controls-dash__select"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
              >
                {STATUS_FILTER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="admin-source-controls-dash__field">
              <span className="admin-source-controls-dash__field-label">Availability</span>
              <select
                className="admin-source-controls-dash__select"
                value={enabledFilter}
                onChange={(event) => setEnabledFilter(event.target.value)}
              >
                <option value="all">All</option>
                <option value="enabled">Enabled only</option>
                <option value="disabled">Disabled only</option>
              </select>
            </label>
            {hasActiveFilters ? (
              <button
                type="button"
                className="admin-source-controls-dash__clear-btn"
                onClick={clearFilters}
              >
                Clear filters
              </button>
            ) : null}
            {hasRunningSource ? (
              <span className="admin-source-controls-dash__live-indicator">
                <span className="admin-dash__pulse" aria-hidden="true" />
                Auto-refreshing
              </span>
            ) : null}
          </div>
        </div>

        {isLoading ? (
          <AdminStatePanel variant="loading" message="Loading sources…" />
        ) : null}

        {!isLoading && errorMessage && typeFilter !== "analytics" && typeFilter !== "finance" && typeFilter !== "vehicle_market" ? (
          <AdminStatePanel
            variant="error"
            message={errorMessage}
            onRetry={() => loadSources()}
          />
        ) : null}

        {!isLoading && typeFilter === "analytics" && ga4ErrorMessage ? (
          <AdminStatePanel
            variant="error"
            message={ga4ErrorMessage}
            onRetry={() => loadSources()}
          />
        ) : null}

        {!isLoading && realEstateErrorMessage && showRealEstateTable ? (
          <AdminStatePanel
            variant="error"
            message={realEstateErrorMessage}
            onRetry={() => loadSources()}
          />
        ) : null}

        {!isLoading && weatherErrorMessage && showWeatherTable ? (
          <AdminStatePanel
            variant="error"
            message={weatherErrorMessage}
            onRetry={() => loadSources()}
          />
        ) : null}

        {showSourceTables ? (
          <div className="admin-source-controls-dash__table-scroll admin-source-controls-dash__merged-grid">
            <p className="admin-ds-page-guide">
              Choose Manage on a source to turn collection on or off, pick how often it runs, start a
              collection, or delete it. Changes are saved from that dialog.
            </p>
            {showRealEstateTable ? (
                <RealEstateSourceControlsTable
                  sources={realEstateSources}
                  filters={filters}
                  onRefresh={loadSources}
                  showToast={showToast}
                  setSources={setRealEstateSources}
                  hideEmpty={hideEmptySections}
                />
            ) : null}

            {showWeatherTable ? (
                <WeatherSourceControlsTable
                  sources={weatherSources}
                  filters={filters}
                  onRefresh={loadSources}
                  showToast={showToast}
                  setSources={setWeatherSources}
                  hideEmpty={hideEmptySections}
                />
            ) : null}

            {showFoodTable ? (
              <>
                <FoodRetailSourceControlsTable
                  sources={filteredRetailFoodSources}
                  filters={filters}
                  hideEmpty
                  onRefresh={loadSources}
                  showToast={showToast}
                  onSourcePatch={(sourceCode, patch) => {
                    setDomainSources((current) => ({
                      ...current,
                      food: current.food.map((source) =>
                        source.sourceCode === sourceCode ? { ...source, ...patch } : source
                      ),
                    }));
                  }}
                />
                <FoodSipsaSourceControlsTable
                  sources={filteredSipsaFoodSources}
                  filters={filters}
                  hideEmpty
                  onRefresh={loadSources}
                  showToast={showToast}
                  onSourcePatch={(sourceCode, patch) => {
                    setDomainSources((current) => ({
                      ...current,
                      food: current.food.map((source) =>
                        source.sourceCode === sourceCode ? { ...source, ...patch } : source
                      ),
                    }));
                  }}
                />
                <FoodInputReferenceSourceControlsTable
                  sources={filteredInputReferenceFoodSources}
                  filters={filters}
                  hideEmpty
                  onRefresh={loadSources}
                  showToast={showToast}
                  onSourcePatch={(sourceCode, patch) => {
                    setDomainSources((current) => ({
                      ...current,
                      food: current.food.map((source) =>
                        source.sourceCode === sourceCode ? { ...source, ...patch } : source
                      ),
                    }));
                  }}
                />
                <FoodOsmPoiSourceControlsTable
                  sources={filteredOsmPoiFoodSources}
                  filters={filters}
                  hideEmpty
                  onRefresh={loadSources}
                  showToast={showToast}
                  onSourcePatch={(sourceCode, patch) => {
                    setDomainSources((current) => ({
                      ...current,
                      food: current.food.map((source) =>
                        source.sourceCode === sourceCode ? { ...source, ...patch } : source
                      ),
                    }));
                  }}
                />
                <FoodBranchLocatorSourceControlsTable
                  sources={filteredBranchLocatorFoodSources}
                  filters={filters}
                  hideEmpty
                  onRefresh={loadSources}
                  showToast={showToast}
                  onSourcePatch={(sourceCode, patch) => {
                    setDomainSources((current) => ({
                      ...current,
                      food: current.food.map((source) =>
                        source.sourceCode === sourceCode ? { ...source, ...patch } : source
                      ),
                    }));
                  }}
                />
                {filteredNonRetailFoodSources.length ? (
                  <FoodSourceControlsGrid
                    sources={filteredNonRetailFoodSources}
                    emptyMessage="No other food sources match the current filters."
                    canManageSources={canManageRealEstateSources}
                    onRefresh={loadSources}
                    showToast={showToast}
                    onSourcePatch={(sourceCode, patch) => {
                      setDomainSources((current) => ({
                        ...current,
                        food: current.food.map((source) =>
                          source.sourceCode === sourceCode ? { ...source, ...patch } : source
                        ),
                      }));
                    }}
                  />
                ) : null}
                {typeFilter === "food" && !filteredFoodSources.length ? (
                  <p className="muted">No food sources match the current filters.</p>
                ) : null}
              </>
            ) : null}

            {showTransportTable ? (
                <TransportSourceControlsTable
                  sources={filteredTransportSources}
                  filters={filters}
                  hideEmpty={hideEmptySections}
                  onRefresh={loadSources}
                  showToast={showToast}
                  onSourcePatch={(sourceCode, patch) => {
                    setDomainSources((current) => ({
                      ...current,
                      transport: current.transport.map((source) =>
                        source.sourceCode === sourceCode ? { ...source, ...patch } : source
                      ),
                    }));
                  }}
                />
            ) : null}

            {showCultureTable ? (
              <>
                {cultureErrorMessage && typeFilter === "culture" ? (
                  <p className="admin-data-health-error">{cultureErrorMessage}</p>
                ) : null}
                <CultureSourceControlsTable
                  sources={filteredCultureSources}
                  filters={filters}
                  hideEmpty={hideEmptySections}
                  onRefresh={loadSources}
                  showToast={showToast}
                  setSources={setCultureSources}
                />
              </>
            ) : null}

            {showVendorDirectoryTable ? (
              <VendorDirectorySourceControlsTable
                sources={domainSources.vendor_directory || []}
                filters={filters}
                hideEmpty={hideEmptySections}
                onRefresh={loadSources}
                showToast={showToast}
                setSources={(updater) => {
                  setDomainSources((current) => ({
                    ...current,
                    vendor_directory:
                      typeof updater === "function"
                        ? updater(current.vendor_directory || [])
                        : updater,
                  }));
                }}
              />
            ) : null}

            {showVehicleMarketTable ? (
                <VehicleMarketSourceControlsTable
                  compact
                  hideEmpty={hideEmptySections}
                  sources={vehicleMarketSources}
                  filters={filters}
                  showToast={showToast}
                  initialErrorMessage={
                    typeFilter === "vehicle_market" ? vehicleMarketErrorMessage : ""
                  }
                  onRefresh={loadSources}
                />
            ) : null}

            {showFinanceTable ? (
              <>
                {!isLoading && financeErrorMessage && typeFilter === "finance" ? (
                  <AdminStatePanel tone="error" message={financeErrorMessage} />
                ) : null}
                <FinanceSourceControlsTable
                  sources={financeSources}
                  filters={filters}
                  hideEmpty={hideEmptySections}
                  onRefresh={loadSources}
                  showToast={showToast}
                  setSources={setFinanceSources}
                />
              </>
            ) : null}

            {showAnalyticsTable ? (
              <>
                {!isLoading && ga4ErrorMessage && typeFilter === "analytics" ? (
                  <AdminStatePanel tone="error" message={ga4ErrorMessage} />
                ) : null}
                <Ga4AnalyticsSourceControlsTable
                  sources={ga4Sources}
                  filters={{ ...filters, statusFilter }}
                  hideEmpty={hideEmptySections}
                  onRefresh={loadSources}
                  showToast={showToast}
                  setSources={setGa4Sources}
                />
              </>
            ) : null}
            <p className="muted admin-source-controls-dash__empty-fallback">
              No data sources match the current filters.
            </p>
          </div>
        ) : null}
      </section>
    </div>
  );
};

export default RealEstateSourceControlsPage;
